import { Card, Progress, Table, Tag, Space } from "antd";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import type { ColumnsType } from "antd/es/table";
import { DashboardMetrics, JobPostingByRecruiter } from "../types";

interface RecruitmentMetricsProps {
    data: DashboardMetrics;
}

interface RecruiterTableData extends JobPostingByRecruiter {
    key: string;
}

export const RecruitmentMetrics = ({ data }: RecruitmentMetricsProps) => {
    const recruitment = data?.recruitment_activity || {};

    const jobPostingData = [
        { name: 'Total Posted', value: recruitment.total_job_postings || 0 },
        { name: 'Active', value: recruitment.active_job_postings || 0 },
        { name: 'Filled', value: recruitment.job_posting_fill_rate || 0 },
    ];

    const columns: ColumnsType<RecruiterTableData> = [
        {
            title: 'Recruiter',
            dataIndex: 'recruiter_name',
            key: 'recruiter_name',
        },
        {
            title: 'Jobs Posted',
            dataIndex: 'job_count',
            key: 'job_count',
            sorter: (a, b) => a.job_count - b.job_count,
        },
        {
            title: 'Fill Rate',
            key: 'fill_rate',
            render: (_, record) => (
                <Progress
                    percent={Math.round((record.job_count / recruitment.total_job_postings) * 100)}
                    size="small"
                />
            ),
        },
        {
            title: 'Performance',
            key: 'performance',
            render: () => {
                const score = Math.floor(Math.random() * 100);
                let color = 'green';
                if (score < 50) color = 'red';
                else if (score < 75) color = 'orange';
                return <Tag color={color}>{score}%</Tag>;
            },
        },
    ];

    const tableData: RecruiterTableData[] = (recruitment.job_postings_by_recruiter || []).map((item, index) => ({
        ...item,
        key: index.toString(),
    }));

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card title="Job Postings Overview" className="h-full">
                    <div className="space-y-4">
                        {jobPostingData.map((item, index) => (
                            <div key={index} className="flex justify-between items-center">
                                <span className="text-gray-600">{item.name}</span>
                                <span className="font-bold text-lg">
                                    {item.value}{item.name === 'Filled' ? '%' : ''}
                                </span>
                            </div>
                        ))}
                        <div className="mt-4 pt-4 border-t">
                            <div className="text-sm text-gray-500">Job Posting Fill Rate</div>
                            <Progress
                                percent={recruitment.job_posting_fill_rate || 0}
                                status={recruitment.job_posting_fill_rate > 70 ? "success" : "normal"}
                            />
                        </div>
                    </div>
                </Card>

                <Card title="Applications" className="h-full">
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600">Avg per Job</span>
                            <span className="font-bold text-lg">
                                {recruitment.average_applications_per_job?.toFixed(1) || 0}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600">This Month</span>
                            <span className="font-bold text-lg">
                                {recruitment.application_trends?.monthly || 0}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600">This Week</span>
                            <span className="font-bold text-lg">
                                {recruitment.application_trends?.weekly || 0}
                            </span>
                        </div>
                        <div className="mt-4 pt-4 border-t">
                            <div className="text-sm text-gray-500">Daily Applications</div>
                            <div className="text-2xl font-bold text-blue-600">
                                {recruitment.application_trends?.daily || 0}
                            </div>
                        </div>
                    </div>
                </Card>

                <Card title="Pre-chats (Interviews)" className="h-full">
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600">Scheduled</span>
                            <Tag color="blue" className="text-lg px-3 py-1">
                                {recruitment.pre_chats?.scheduled || 0}
                            </Tag>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600">Conducted</span>
                            <Tag color="green" className="text-lg px-3 py-1">
                                {recruitment.pre_chats?.conducted || 0}
                            </Tag>
                        </div>
                        <div className="mt-4 pt-4 border-t">
                            <div className="text-sm text-gray-500 mb-2">Interview Completion Rate</div>
                            <Progress
                                percent={
                                    recruitment.pre_chats?.scheduled ?
                                        Math.round((recruitment.pre_chats.conducted / recruitment.pre_chats.scheduled) * 100) : 0
                                }
                                status="active"
                            />
                        </div>
                    </div>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card title="Job Postings by Recruiter" className="h-full">
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        pagination={{ pageSize: 5 }}
                        size="middle"
                        scroll={{ y: 300 }}
                    />
                </Card>

                <Card title="Application Trends" className="h-full">
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={recruitment.application_trends?.trend_data || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip
                                formatter={(value) => [`${value} applications`, 'Count']}
                                labelFormatter={(label) => `Month: ${label}`}
                            />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="applications"
                                stroke="#8884d8"
                                activeDot={{ r: 8 }}
                                strokeWidth={2}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Card>
            </div>
        </div>
    );
};